'use client';

import { useGsapContext } from 'hooks/animation/useGsapContext';
import gsap from 'gsap';
import { useMasterTimeline } from 'hooks/animation/useMasterTimeline';
import { useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
// import { useWindowSize } from 'hooks/dom/useWindowSize';
import { CustomImage } from '../..';
import styles from './BgBlurredText.module.css';

export const Content1 = ({ className }: { className?: string }) => {
  const gsapContext = useGsapContext();
  const { add } = useMasterTimeline();
  const container = useRef<HTMLDivElement>(null);
  const intersectionObserver = useRef<IntersectionObserver | null>(null);

  const animationInit = () => {
    if (!container.current || !window) return;
    gsapContext.add(() => {
      const tl = gsap.timeline({
        defaults: {
          duration: 0.5,
          ease: 'power2.easeInOut',
        },
        scrollTrigger: {
          trigger: container.current,
          start: 'top center ',
          end: '50px',
          scrub: 0.2,
          immediateRender: false,
        },
      });
      tl.fromTo(
        'h3 > span',
        {
          autoAlpha: 0,
        },
        {
          autoAlpha: 1,
          duration: 0.35,
          stagger: 0.05, // Increased the stagger value for more delay
        },
        0.2,
      );
      tl.set('h3 > span', { autoAlpha: 1 });

      add(tl, 'buy-bg-blurred-content1');
    });
  };

  useIsomorphicLayoutEffect(() => {
    intersectionObserver.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          animationInit();
        }
      },
      {
        root: null,
        threshold: 0,
      },
    );

    intersectionObserver.current.observe(container.current as Element);

    return () => {
      if (intersectionObserver.current) {
        intersectionObserver.current.disconnect();
      }
    };
  }, []);

  return (
    <div
      ref={container}
      className={className ? `${styles.content1Root} ${className}` : styles.content1Root}
    >
      <h3>
        {"Moves with you. Styled with Diesel's attitude. Powered by your body."
          .split(' ')
          .map((text, index) => {
            return <span key={`detail-text-${index}`}>{text} </span>;
          })}
      </h3>
    </div>
  );
};

export const Content2 = ({ className }: { className?: string }) => {
  const gsapContext = useGsapContext();
  const { add } = useMasterTimeline();
  const container = useRef<HTMLDivElement>(null);
  const intersectionObserver = useRef<IntersectionObserver | null>(null);

  const configs = [
    {
      title: <>Phantom</>,
      description:
        "Uniting Diesel's iconic Double-D design with Ultrahuman's advanced technology, the Phantom variant, finished in a dark Distressed tone, embodies an elusivE and gritty presence. Industrial at  heart, refined in form.",
    },
    {
      title: <>Cryo</>,
      description:
        "Bringing Diesel's iconic Double-D design to Ultrahuman's advanced technology, the shiny Cryo variant reflects the integration of protocols in the bleeding edge of health and fashion. Icy in tone, effortless in presence.",
    },
    // {
    //   title: <>Ultimate{!isMobile ? <br /> : null} comfort</>,
    //   description:
    //     'Crafted with precision, featuring a hypoallergenic smooth inner lining for unmatched comfort and a barely-there feel.',
    // },
  ];

  const animationInit = () => {
    if (!container.current || !window) return;
    gsapContext.add(() => {
      const tl = gsap.timeline({
        defaults: {
          duration: 0.5,
          ease: 'power2.easeInOut',
        },
        scrollTrigger: {
          trigger: container.current,
          start: 'top center ',
          end: '50px',
          scrub: 0.2,
          immediateRender: false,
        },
      });
      tl.fromTo(
        '.config-contents',
        {
          autoAlpha: 0,
        },
        {
          autoAlpha: 1,
          duration: 0.35,
          stagger: 0.06, // Increased the stagger value for more delay
        },
        0.2,
      );
      // t103.set('.config-contents', { autoAlpha: 1 });

      add(tl, 'buy-bg-blurred-content2');
    });
  };

  useIsomorphicLayoutEffect(() => {
    intersectionObserver.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          animationInit();
        }
      },
      {
        root: null,
        threshold: 0,
      },
    );

    intersectionObserver.current.observe(container.current as Element);

    return () => {
      if (intersectionObserver.current) {
        intersectionObserver.current.disconnect();
      }
    };
  }, []);

  return (
    <div
      ref={container}
      className={className ? `${styles.content2Root} ${className}` : styles.content2Root}
    >
      <div className="config-contents">
        {configs.map((config, index) => {
          return (
            <div key={`config-${index}`} className="config">
              <h3>{config.title}</h3>
              <p>{config.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const BgBlurredText = ({
  className,
  htmlId,
}: {
  className?: string;
  htmlId?: string;
}) => {
  return (
    <div
      className={className ? `${styles.bgRoot} ${className}` : styles.bgRoot}
      id={htmlId}
    >
      <CustomImage
        src="web_v2/uh-x-diesel/buy-page/bg/diesel-denim-bg.png"
        alt=""
        fill
        className="img-container"
      />
      <div className="backdrop"></div>

      <div className="content content-1">
        <Content1 />
      </div>
      <div className="content content-2">
        <Content2 />
      </div>
    </div>
  );
};
